/**
 * GYM-1588 — Declarative nav config as single source of truth.
 *
 * Defines every workspace, its sections, and its nav items in one place so
 * Sidebar, TopBar, and MobileBottomNav can all consume the same data instead of
 * duplicating permission checks and route lists across components.
 *
 * Phase 0 only: data + helpers. No render change yet.
 * See document/ui_design_change/navigation-redesign-v2.md §5 Phase 0.
 */

import type { ComponentType } from "react";
import {
  Rocket,
  AlertTriangle,
  Apple,
  Archive,
  BarChart3,
  Bell,
  BookOpen,
  Building2,
  Calendar,
  CalendarClock,
  CalendarDays,
  ClipboardCheck,
  ClipboardList,
  Clock,
  Code2,
  Cog,
  Cpu,
  CreditCard,
  DollarSign,
  Dumbbell,
  Eye,
  FileText,
  FileX,
  Flag,
  GitCompare,
  GraduationCap,
  Landmark,
  LayoutDashboard,
  LayoutList,
  LineChart,
  Lock,
  LogIn,
  Mail,
  MapPin,
  Medal,
  Megaphone,
  Monitor,
  Package,
  PackageSearch,
  Palette,
  RefreshCcw,
  Salad,
  ScrollText,
  Settings,
  Share2,
  Shield,
  ShoppingCart,
  SlidersHorizontal,
  Soup,
  Store,
  Table2,
  Tag,
  TrendingDown,
  TrendingUp,
  UserCheck,
  UserMinus,
  UserRound,
  Users,
  Wallet,
  Wrench,
} from "lucide-react";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * A predicate that receives a function to check a single permission code and
 * returns true when the current user may see this item. Undefined means always
 * visible (no permission gate needed).
 *
 * Design: keeps permission logic testable without React hooks — callers supply
 * a `has` function derived from `useEffectivePermissions().byCode`.
 */
export type PermFn = (has: (key: string) => boolean) => boolean;

export interface NavItem {
  readonly to: string;
  readonly labelKey: string;
  readonly icon: ComponentType<{ className?: string }>;
  /** Undefined → always visible to authenticated users. */
  readonly permission?: PermFn;
}

export interface NavSection {
  readonly key: string;
  readonly labelKey: string;
  readonly items: readonly NavItem[];
}

export interface Workspace {
  readonly key: string;
  readonly labelKey: string;
  readonly icon: ComponentType<{ className?: string }>;
  /**
   * The route to navigate to when this workspace is selected from the top
   * header (used by TopBar in Phase 1 and workspaceForPath fallback).
   */
  readonly defaultPath: string;
  /**
   * Empty array = single-destination workspace (Dashboard, Reports).
   * The workspace is always visible in that case.
   */
  readonly sections: readonly NavSection[];
}

// ---------------------------------------------------------------------------
// Nav config — all 8 workspaces
// ---------------------------------------------------------------------------

export const NAV_CONFIG: readonly Workspace[] = [
  // ── DASHBOARD ─────────────────────────────────────────────────────────────
  {
    key: "dashboard",
    labelKey: "nav.workspace.dashboard",
    icon: LayoutDashboard,
    defaultPath: "/",
    sections: [], // single-destination; no sidebar groups
  },

  // ── FRONT DESK ────────────────────────────────────────────────────────────
  {
    key: "front-desk",
    labelKey: "nav.workspace.frontDesk",
    icon: Building2,
    defaultPath: "/members",
    sections: [
      {
        key: "front-desk.members",
        labelKey: "nav.section.members",
        items: [
          {
            to: "/members",
            labelKey: "nav.members",
            icon: Users,
            permission: (has) => has("members.read"),
          },
          {
            to: "/check-in",
            labelKey: "nav.checkIn",
            icon: LogIn,
            permission: (has) =>
              has("checkin.event.write") || has("checkin.event.read"),
          },
          {
            to: "/members/adjustments/pending",
            labelKey: "nav.pendingAdjustments",
            icon: SlidersHorizontal,
            permission: (has) => has("billing.ledger.adjust.approve"),
          },
          {
            to: "/gamification/leaderboard",
            labelKey: "nav.gamifLeaderboard",
            icon: Medal,
            permission: (has) => has("gamification.leaderboard.read"),
          },
        ],
      },
      {
        key: "front-desk.memberships",
        labelKey: "nav.section.membershipsAndSales",
        items: [
          {
            to: "/memberships",
            labelKey: "nav.memberships",
            icon: CreditCard,
            permission: (has) =>
              has("plans.read") || has("subscriptions.read"),
          },
          {
            to: "/subscriptions/review-queue",
            labelKey: "nav.reviewQueue",
            icon: ClipboardList,
            permission: (has) => has("subscriptions.selfservice.review"),
          },
          {
            to: "/retention",
            labelKey: "nav.retention",
            icon: UserMinus,
            permission: (has) => has("retention.savelist.view"),
          },
        ],
      },
      {
        key: "front-desk.leads",
        labelKey: "nav.section.leads",
        items: [
          {
            to: "/leads",
            labelKey: "nav.leads",
            icon: UserCheck,
            permission: (has) => has("crm.lead.read"),
          },
          {
            to: "/leads/trials",
            labelKey: "nav.trialsWorklist",
            icon: ClipboardList,
            permission: (has) => has("crm.trial.issue"),
          },
          {
            to: "/settings/crm",
            labelKey: "nav.settingsNav.crm",
            icon: UserCheck,
            permission: (has) => has("crm.lead.read"),
          },
          {
            to: "/settings/trial-templates",
            labelKey: "nav.settingsNav.trialTemplates",
            icon: FileText,
            permission: (has) => has("crm.lead.read"),
          },
        ],
      },
      {
        key: "front-desk.activity",
        labelKey: "nav.section.activity",
        items: [
          {
            to: "/notifications/center",
            labelKey: "nav.notificationCenter",
            icon: Bell,
          },
        ],
      },
      {
        key: "front-desk.settings",
        labelKey: "nav.section.frontDeskSettings",
        items: [
          {
            to: "/settings/plans",
            labelKey: "nav.settingsNav.plans",
            icon: CreditCard,
            permission: (has) => has("plans.read"),
          },
          {
            to: "/settings/promotions",
            labelKey: "nav.settingsNav.promotions",
            icon: Tag,
            permission: (has) => has("plans.read"),
          },
          {
            to: "/settings/dynamic-pricing",
            labelKey: "nav.settingsNav.dynamicPricing",
            icon: TrendingUp,
            permission: (has) => has("pricing.manage"),
          },
          {
            to: "/settings/referral-program",
            labelKey: "nav.settingsNav.referralProgram",
            icon: Share2,
            permission: (has) => has("plans.read"),
          },
          {
            to: "/settings/member-document-visibility",
            labelKey: "nav.settingsNav.memberDocVisibility",
            icon: Eye,
            permission: (has) => has("members.read"),
          },
        ],
      },
    ],
  },

  // ── TRAINING ──────────────────────────────────────────────────────────────
  {
    key: "training",
    labelKey: "nav.workspace.training",
    icon: GraduationCap,
    defaultPath: "/classes",
    sections: [
      {
        key: "training.classes",
        labelKey: "nav.section.classes",
        items: [
          {
            to: "/classes",
            labelKey: "nav.classes",
            icon: Calendar,
            permission: (has) =>
              has("pt_sessions.read") ||
              has("pt.booking.read") ||
              has("pt_sessions.consume"),
          },
          {
            to: "/classes/templates",
            labelKey: "nav.classTemplates",
            icon: LayoutList,
            permission: (has) => has("classes.template.manage"),
          },
          {
            to: "/classes/penalties",
            labelKey: "nav.classPenalties",
            icon: ClipboardList,
            permission: (has) => has("classes.booking.read"),
          },
          {
            to: "/staff/class-requests",
            labelKey: "nav.classRequests",
            icon: ClipboardCheck,
            permission: (has) => has("classes.booking.write"),
          },
        ],
      },
      {
        key: "training.content",
        labelKey: "nav.section.content",
        items: [
          {
            to: "/programs",
            labelKey: "nav.programs",
            icon: BookOpen,
            permission: (has) => has("programs.read"),
          },
          {
            to: "/workouts",
            labelKey: "nav.workouts",
            icon: Dumbbell,
            permission: (has) => has("workouts.read"),
          },
          {
            to: "/nutrition/plans",
            labelKey: "nav.nutritionPlans",
            icon: Salad,
            permission: (has) => has("nutrition.read"),
          },
          {
            to: "/nutrition/meals",
            labelKey: "nav.nutritionMeals",
            icon: Soup,
            permission: (has) => has("nutrition.read"),
          },
          {
            to: "/nutrition/foods",
            labelKey: "nav.nutritionFoods",
            icon: Apple,
            permission: (has) => has("nutrition.read"),
          },
          {
            to: "/exercises",
            labelKey: "nav.exercises",
            icon: Dumbbell,
            permission: (has) => has("workouts.read"),
          },
          // diet-plans nav item removed — feature not yet built.
        ],
      },
      {
        key: "training.settings",
        labelKey: "nav.section.trainingSettings",
        items: [
          {
            to: "/settings/class-attendance",
            labelKey: "nav.settingsNav.classAttendance",
            icon: Calendar,
            permission: (has) => has("classes.booking.read"),
          },
        ],
      },
    ],
  },

  // ── TEAM ──────────────────────────────────────────────────────────────────
  {
    key: "team",
    labelKey: "nav.workspace.team",
    icon: Users,
    defaultPath: "/staff",
    sections: [
      {
        key: "team.staff",
        labelKey: "nav.section.staff",
        items: [
          {
            to: "/staff",
            labelKey: "nav.staff",
            icon: UserRound,
            permission: (has) => has("staff.staff.read"),
          },
          {
            to: "/staff/todays-sessions",
            labelKey: "nav.todaysSessions",
            icon: ClipboardList,
            permission: (has) => has("pt_sessions.consume"),
          },
          {
            to: "/staff/pt-requests",
            labelKey: "nav.ptRequests",
            icon: ClipboardCheck,
            permission: (has) => has("pt.bookings.manage"),
          },
          {
            to: "/clock",
            labelKey: "nav.clock",
            icon: Clock,
            permission: (has) => has("staff.timesheet.clock.self"),
          },
          {
            to: "/timesheets",
            labelKey: "nav.timesheets",
            icon: Table2,
            permission: (has) => has("staff.timesheet.read"),
          },
          {
            to: "/staff/review-moderation",
            labelKey: "nav.reviewModeration",
            icon: Flag,
            permission: (has) => has("pt.review.moderate"),
          },
          {
            to: "/staff/review-questions",
            labelKey: "nav.reviewQuestions",
            icon: ClipboardList,
            permission: (has) => has("pt.review.moderate"),
          },
        ],
      },
      {
        key: "team.performance",
        labelKey: "nav.section.performance",
        items: [
          {
            to: "/trainers/kpis",
            labelKey: "nav.trainerKpis",
            icon: BarChart3,
            permission: (has) => has("pt.booking.read.self"),
          },
          {
            to: "/trainers/comparison",
            labelKey: "nav.trainerComparison",
            icon: BarChart3,
            permission: (has) => has("pt.booking.read"),
          },
        ],
      },
      {
        key: "team.payroll",
        labelKey: "nav.section.payroll",
        items: [
          {
            to: "/payroll",
            labelKey: "nav.payroll",
            icon: DollarSign,
            permission: (has) => has("staff.payroll.view"),
          },
        ],
      },
      {
        key: "team.leave",
        labelKey: "nav.section.leave",
        items: [
          {
            to: "/leave/requests",
            labelKey: "nav.leaveRequests",
            icon: CalendarDays,
            permission: (has) => has("leave.request.read"),
          },
          {
            to: "/leave/approvals",
            labelKey: "nav.leaveApprovals",
            icon: ClipboardCheck,
            permission: (has) => has("leave.request.approve"),
          },
        ],
      },
      {
        key: "team.activity",
        labelKey: "nav.section.activity",
        items: [
          {
            to: "/notifications/center",
            labelKey: "nav.notificationCenter",
            icon: Bell,
            permission: (has) => has("notifications.read"),
          },
        ],
      },
    ],
  },

  // ── FINANCE ───────────────────────────────────────────────────────────────
  {
    key: "finance",
    labelKey: "nav.workspace.finance",
    icon: TrendingUp,
    defaultPath: "/pos",
    sections: [
      {
        key: "finance.salesReporting",
        labelKey: "nav.section.sales",
        items: [
          {
            to: "/pos",
            labelKey: "nav.pos",
            icon: ShoppingCart,
            permission: (has) => has("pos.product.read"),
          },
          {
            to: "/pos/stock",
            labelKey: "nav.posStock",
            icon: Package,
            permission: (has) => has("pos.stock.read"),
          },
          {
            to: "/pos/shifts",
            labelKey: "nav.posShifts",
            icon: Clock,
            permission: (has) => has("pos.shift.write"),
          },
          {
            to: "/pos/sales",
            labelKey: "nav.salesHistory",
            icon: ScrollText,
            permission: (has) => has("pos.sale.read"),
          },
          {
            to: "/sales/daily-report",
            labelKey: "nav.dailySalesReport",
            icon: BarChart3,
            permission: (has) => has("pos.sale.read"),
          },
          {
            to: "/sales/payments-received",
            labelKey: "nav.paymentsReceived",
            icon: Wallet,
            permission: (has) => has("payments.read"),
          },
          {
            to: "/sales/credit-notes",
            labelKey: "nav.creditNotes",
            icon: FileText,
            permission: (has) => has("payments.read"),
          },
          // hidden: Take Payment removed from sidebar
          // {
          //   to: "/payments",
          //   labelKey: "nav.takePayment",
          //   icon: CreditCard,
          //   permission: (has) => has("payments.record"),
          // },
          {
            to: "/cashbook",
            labelKey: "nav.cashbook",
            icon: BookOpen,
            permission: (has) => has("cashbook.read") || has("cashbook.manage"),
          },
        ],
      },
      {
        key: "finance.purchase",
        labelKey: "nav.section.purchase",
        items: [
          {
            to: "/settings/suppliers",
            labelKey: "nav.suppliers",
            icon: Archive,
            permission: (has) => has("inventory.supplier.read"),
          },
          {
            to: "/inventory/purchase-orders",
            labelKey: "nav.purchaseOrders",
            icon: Package,
            permission: (has) => has("inventory.po.read"),
          },
          {
            to: "/inventory/debit-notes",
            labelKey: "nav.debitNotes",
            icon: FileX,
            permission: (has) => has("inventory.po.read"),
          },
          {
            to: "/inventory/accounts-payable",
            labelKey: "nav.accountsPayable",
            icon: FileText,
            permission: (has) => has("inventory.ap.read") || has("inventory.ap.write"),
          },
          {
            to: "/inventory/reorder-forecast",
            labelKey: "nav.reorderForecast",
            icon: TrendingDown,
            permission: (has) => has("pos.stock.read"),
          },
        ],
      },
      {
        key: "finance.items",
        labelKey: "nav.section.items",
        items: [
          {
            to: "/finance/items/products",
            labelKey: "nav.settingsNav.productCatalog",
            icon: Package,
            permission: (has) => has("pos.product.read"),
          },
          {
            to: "/finance/items/plans",
            labelKey: "nav.settingsNav.plans",
            icon: CreditCard,
            permission: (has) => has("plans.read"),
          },
          {
            to: "/finance/items/classes",
            labelKey: "nav.classTemplates",
            icon: LayoutList,
            permission: (has) => has("classes.template.manage"),
          },
          {
            to: "/finance/items/zones",
            labelKey: "nav.zones",
            icon: MapPin,
            permission: (has) => has("maintenance.asset.read"),
          },
          {
            to: "/finance/items/pt-bookings",
            labelKey: "nav.ptBookings",
            icon: Dumbbell,
            permission: (has) => has("pt.booking.read"),
          },
          {
            to: "/inventory/stock-takes",
            labelKey: "nav.stockTakes",
            icon: ClipboardList,
            permission: (has) => has("inventory.stocktake.write") || has("inventory.stocktake.read"),
          },
        ],
      },
      {
        key: "finance.settings",
        labelKey: "nav.section.financeSettings",
        items: [
          {
            to: "/settings/stores",
            labelKey: "nav.settingsNav.stores",
            icon: Store,
            permission: (has) => has("pos.product.read"),
          },
          {
            to: "/settings/payment-gateways",
            labelKey: "nav.settingsNav.paymentGateways",
            icon: Landmark,
            permission: (has) =>
              has("payments.read") || has("payments.record"),
          },
          {
            to: "/settings/accounting",
            labelKey: "nav.settingsNav.accountingMapping",
            icon: FileText,
            permission: (has) =>
              has("payments.read") || has("payments.record"),
          },
        ],
      },
    ],
  },

  // ── OPERATIONS ────────────────────────────────────────────────────────────
  {
    key: "operations",
    labelKey: "nav.workspace.operations",
    icon: Cog,
    defaultPath: "/ops/incidents",
    sections: [
      {
        key: "operations.facility",
        labelKey: "nav.section.facility",
        items: [
          {
            to: "/ops/incidents",
            labelKey: "nav.incidents",
            icon: AlertTriangle,
            permission: (has) => has("ops.incident.read"),
          },
          {
            to: "/ops/lost-and-found",
            labelKey: "nav.lostAndFound",
            icon: PackageSearch,
            permission: (has) => has("ops.incident.read"),
          },
          {
            to: "/maintenance/assets",
            labelKey: "nav.assetRegister",
            icon: Wrench,
            permission: (has) => has("maintenance.asset.read"),
          },
          {
            to: "/maintenance/zones",
            labelKey: "nav.zones",
            icon: MapPin,
            permission: (has) => has("maintenance.asset.read"),
          },
        ],
      },
      {
        key: "operations.engagement",
        labelKey: "nav.section.engagement",
        items: [
          {
            to: "/marketing/campaigns",
            labelKey: "nav.marketing",
            icon: Megaphone,
            permission: (has) => has("notifications.campaign.manage"),
          },
          {
            to: "/notifications/history",
            labelKey: "nav.notificationsHistory",
            icon: Bell,
            permission: (has) => has("notifications.read"),
          },
        ],
      },
      {
        key: "operations.settings",
        labelKey: "nav.section.operationsSettings",
        items: [
          {
            to: "/settings/digest",
            labelKey: "nav.settingsNav.dailyDigest",
            icon: Mail,
            permission: (has) => has("notifications.read"),
          },
        ],
      },
    ],
  },

  // ── REPORTS ───────────────────────────────────────────────────────────────
  {
    key: "reports",
    labelKey: "nav.workspace.reports",
    icon: BarChart3,
    defaultPath: "/reports",
    sections: [
      {
        key: "reports.financial",
        labelKey: "nav.section.reportsFinancial",
        items: [
          { to: "/reports/revenue-by-period",        labelKey: "nav.reports.revenueByPeriod",        icon: TrendingUp,    permission: (has) => has("reports.financial") },
          { to: "/reports/profit-loss",              labelKey: "nav.reports.profitLoss",              icon: FileText,      permission: (has) => has("reports.financial") },
          { to: "/reports/vat",                      labelKey: "nav.reports.vat",                     icon: FileText,      permission: (has) => has("reports.vat.view") },
          { to: "/reports/dues-aging",               labelKey: "nav.reports.duesAging",               icon: TrendingDown,  permission: (has) => has("reports.financial") },
          { to: "/reports/deferred-revenue",         labelKey: "nav.reports.deferredRevenue",         icon: BookOpen,      permission: (has) => has("reports.financial") },
          { to: "/reports/accounting-exports",       labelKey: "nav.reports.accountingExports",       icon: BarChart3,     permission: (has) => has("reports.financial") },
          { to: "/reports/payout-reconciliation",    labelKey: "nav.reports.payoutReconciliation",    icon: Wallet,        permission: (has) => has("reports.financial") },
        ],
      },
      {
        key: "reports.members",
        labelKey: "nav.section.reportsMembers",
        items: [
          { to: "/reports/members-by-status",        labelKey: "nav.reports.membersByStatus",        icon: Users,         permission: (has) => has("reports.members") },
          { to: "/reports/plan-mix",                 labelKey: "nav.reports.planMix",                icon: LayoutList,    permission: (has) => has("reports.members") },
          { to: "/reports/cohort-analysis",          labelKey: "nav.reports.cohortAnalysis",         icon: LineChart,     permission: (has) => has("reports.members") },
          { to: "/reports/failed-renewals",          labelKey: "nav.reports.failedRenewals",         icon: RefreshCcw,    permission: (has) => has("reports.members") },
          { to: "/reports/corporate-renewal-pipeline", labelKey: "nav.reports.renewalPipeline",      icon: Building2,     permission: (has) => has("reports.members") },
        ],
      },
      {
        key: "reports.operations",
        labelKey: "nav.section.reportsOperations",
        items: [
          { to: "/reports/checkin-log",               labelKey: "nav.reports.checkinLog",             icon: LogIn,         permission: (has) => has("reports.checkin_log.view") },
          { to: "/reports/leave-report",              labelKey: "nav.reports.leaveReport",            icon: CalendarDays,  permission: (has) => has("reports.leave_report.view") },
          { to: "/reports/payroll-summary",          labelKey: "nav.reports.payrollSummary",         icon: DollarSign,    permission: (has) => has("reports.operations") },
          { to: "/reports/promo-redemption",         labelKey: "nav.reports.promoCodes",             icon: Tag,           permission: (has) => has("reports.operations") },
          { to: "/reports/low-stock",                labelKey: "nav.reports.lowStock",               icon: Package,       permission: (has) => has("reports.operations") },
          { to: "/reports/leaderboards",             labelKey: "nav.reports.leaderboards",           icon: Medal,         permission: (has) => has("reports.operations") },
        ],
      },
      {
        key: "reports.tools",
        labelKey: "nav.section.reportsTools",
        items: [
          { to: "/reports/compare",                  labelKey: "nav.reports.compare",                icon: GitCompare,    permission: (has) => has("reports.tools") },
          { to: "/reports/builder",                  labelKey: "nav.reports.builder",                icon: LayoutList,    permission: (has) => has("reports.tools") },
          { to: "/reports/saved",                    labelKey: "nav.reports.saved",                  icon: BookOpen,      permission: (has) => has("reports.tools") },
          { to: "/reports/schedules",                labelKey: "nav.reports.schedules",              icon: CalendarClock, permission: (has) => has("reports.tools") },
        ],
      },
    ],
  },

  // ── ADMIN ─────────────────────────────────────────────────────────────────
  {
    key: "admin",
    labelKey: "nav.workspace.admin",
    icon: Settings,
    defaultPath: "/settings",
    sections: [
      {
        key: "admin.organization",
        labelKey: "nav.section.organization",
        items: [
          {
            to: "/getting-started",
            labelKey: "nav.gettingStarted",
            icon: Rocket,
          },
          {
            to: "/settings/branches",
            labelKey: "nav.settingsNav.branches",
            icon: Building2,
            // No permission gate — branch overview is always visible to authenticated users,
            // matching the test expectation that admin workspace stays accessible with no perms.
          },
          {
            to: "/settings/users",
            labelKey: "nav.settingsNav.users",
            icon: Users,
            permission: (has) => has("users.read"),
          },
          {
            to: "/settings/roles",
            labelKey: "nav.settingsNav.roles",
            icon: Shield,
            permission: (has) => has("roles.read"),
          },
          {
            to: "/settings/badge-catalog",
            labelKey: "nav.badgeCatalog",
            icon: Medal,
            permission: (has) => has("gamification.badge.manage"),
          },
        ],
      },
      {
        key: "admin.selfRegistration",
        labelKey: "nav.section.selfRegistration",
        items: [
          {
            to: "/settings/self-registration",
            labelKey: "nav.settingsNav.selfRegistration",
            icon: UserCheck,
            permission: (has) => has("tenant.settings.operations"),
          },
          {
            to: "/members/self-reg-queue",
            labelKey: "nav.selfRegQueue",
            icon: ClipboardList,
            permission: (has) => has("members.self_registration.review"),
          },
        ],
      },
      {
        key: "admin.security",
        labelKey: "nav.section.security",
        items: [
          {
            to: "/settings/security/2fa",
            labelKey: "nav.settingsNav.twoFactor",
            icon: Lock,
            permission: (has) => has("tenant.security.manage") || has("auth.2fa.reset.manage"),
          },
          {
            to: "/settings/security/policy",
            labelKey: "nav.settingsNav.securityPolicy",
            icon: Shield,
            permission: (has) => has("tenant.security.manage"),
          },
          {
            to: "/settings/audit",
            labelKey: "nav.settingsNav.auditLog",
            icon: ScrollText,
            permission: (has) => has("audit.read"),
          },
          {
            to: "/settings/audit-retention",
            labelKey: "nav.settingsNav.auditRetention",
            icon: Archive,
            permission: (has) => has("audit.retention.manage"),
          },
        ],
      },
      {
        key: "admin.devices",
        labelKey: "nav.section.devices",
        items: [
          {
            to: "/settings/devices",
            labelKey: "nav.settingsNav.accessDevices",
            icon: Cpu,
            permission: (has) => has("access.device.read"),
          },
          {
            to: "/settings/kiosk-devices",
            labelKey: "nav.settingsNav.kioskDevices",
            icon: Monitor,
            permission: (has) => has("kiosk.devices.read") || has("kiosk.devices.manage"),
          },
        ],
      },
      {
        key: "admin.leave",
        labelKey: "nav.section.leaveSettings",
        items: [
          {
            to: "/leave/types",
            labelKey: "nav.settingsNav.leaveTypes",
            icon: CalendarDays,
            permission: (has) => has("leave.type.read"),
          },
        ],
      },
      {
        key: "admin.branding",
        labelKey: "nav.section.branding",
        items: [
          {
            to: "/settings/branding",
            labelKey: "nav.settingsNav.branding",
            icon: Palette,
            permission: (has) => has("tenant.settings.operations"),
          },
        ],
      },
      {
        key: "admin.developer",
        labelKey: "nav.section.developer",
        items: [
          {
            to: "/settings/developer",
            labelKey: "nav.settingsNav.developer",
            icon: Code2,
            permission: (has) => has("tenant.settings.finance") || has("tenant.settings.operations"),
          },
        ],
      },
    ],
  },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Items in a section that the user may see. */
export function visibleItems(
  items: readonly NavItem[],
  has: (key: string) => boolean,
): NavItem[] {
  return items.filter((item) => item.permission == null || item.permission(has));
}

/** Sections of a workspace that have at least one visible item. */
export function visibleSections(
  sections: readonly NavSection[],
  has: (key: string) => boolean,
): NavSection[] {
  return sections
    .map((s) => ({ ...s, items: visibleItems(s.items, has) }))
    .filter((s) => s.items.length > 0);
}

/**
 * Workspaces the user may see.
 * - Single-destination workspaces (empty sections) are always visible.
 * - Multi-section workspaces are visible only when ≥1 section survives RBAC.
 */
export function visibleWorkspaces(
  workspaces: readonly Workspace[],
  has: (key: string) => boolean,
): Workspace[] {
  return workspaces.filter((ws) => {
    if (ws.sections.length === 0) return true;
    return visibleSections(ws.sections, has).length > 0;
  });
}

/**
 * Returns the first path the user can access in a workspace.
 * Falls back to ws.defaultPath if nothing is accessible (e.g. permissions still loading).
 */
export function firstAccessiblePath(
  ws: Workspace,
  has: (key: string) => boolean,
): string {
  for (const section of ws.sections) {
    for (const item of section.items) {
      if (item.permission == null || item.permission(has)) {
        return item.to;
      }
    }
  }
  return ws.defaultPath;
}

/**
 * Returns the workspace whose items or defaultPath best matches `pathname`.
 *
 * Uses longest-prefix matching: a deeper item path wins over a shallower
 * workspace defaultPath, so `/settings/crm` → Front Desk (not Admin).
 *
 * The root path `/` is matched exactly to avoid Dashboard swallowing every
 * unmatched route.
 */
export function workspaceForPath(pathname: string): Workspace | undefined {
  type Candidate = { path: string; ws: Workspace };
  const candidates: Candidate[] = [];

  for (const ws of NAV_CONFIG) {
    if (ws.defaultPath) {
      candidates.push({ path: ws.defaultPath, ws });
    }
    for (const section of ws.sections) {
      for (const item of section.items) {
        candidates.push({ path: item.to, ws });
      }
    }
  }

  // Longest path first so more-specific item matches win over defaultPath fallbacks
  candidates.sort((a, b) => b.path.length - a.path.length);

  for (const { path, ws } of candidates) {
    if (path === "/") {
      if (pathname === "/") return ws;
    } else if (pathname === path || pathname.startsWith(path + "/")) {
      return ws;
    }
  }
  return undefined;
}
